import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import localforage from 'localforage';
import { 
  LineChart, Line, PieChart, Pie, Tooltip, Legend, Cell, 
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, BarChart, Bar 
} from 'recharts';
import {
  User, Calendar, Clock, AlertTriangle, Save, X, Edit3, Mail, 
  AlertCircle, CheckCircle, Image as ImageIcon, Activity,
  Calendar as CalendarIcon, Clock as ClockIcon, TrendingUp,
  Trash2, AlertOctagon
} from 'lucide-react';

const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

const STATUS_TYPES = {
  ACTIVE: { color: '#10b981', label: 'Aktiv' },
  CHECKEDOUT: { color: '#3b82f6', label: 'Abgemeldet' },
  SICK: { color: '#ef4444', label: 'Krank' },
  'Nicht-Abgemeldet': { color: '#f59e0b', label: 'Nicht Abgemeldet' }
};

const UserModal = ({ user = null, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [statusHistory, setStatusHistory] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');

  useEffect(() => {
    if (user?.id) {
      fetchUserData();
      loadProfileImage();
    }
  }, [user?.id]);

  const renderStatusChart = () => {
    if (!statusHistory.length) return null;

    const statusCounts = statusHistory.reduce((acc, record) => {
      acc[record.newStatus] = (acc[record.newStatus] || 0) + 1;
      return acc;
    }, {});

    const pieData = Object.entries(statusCounts).map(([status, count]) => ({
      name: STATUS_TYPES[status]?.label || status,
      value: count,
      status: status
    }));

    return (
      <div style={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {pieData.map((entry, index) => (
                <Cell 
                  key={index} 
                  fill={STATUS_TYPES[entry.status]?.color || '#6b7280'} 
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderTimelineChart = () => {
    if (!statusHistory.length) return null;

    const timelineData = statusHistory.map(record => ({
      date: new Date(record.timestamp).toLocaleDateString(),
      status: record.newStatus,
      updatedBy: record.updatedBy
    }));

    return (
      <div style={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <Tooltip />
            <Line
              type="stepAfter"
              dataKey="status"
              stroke="#8884d8"
              strokeWidth={2}
              dot={{ fill: '#8884d8' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const loadProfileImage = async () => {
    if (!user?.profileImagePath) {
      setImageLoaded(false);
      return;
    }

    try {
      const token = await localforage.getItem('token');
      const timestamp = new Date().getTime();
      const imageUrl = `${BASE_URL}${user.profileImagePath}?t=${timestamp}`;
      
      const img = new Image();
      img.onload = () => {
        setProfileImageUrl(imageUrl);
        setImageLoaded(true);
      };
      img.onerror = () => {
        setImageLoaded(false);
      };
      img.src = imageUrl;
    } catch (err) {
      setImageLoaded(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      // Fetch user statistics
      const statsResponse = await fetch(
        `${BASE_URL}/api/users/${user.id}/statistics`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!statsResponse.ok) throw new Error('Failed to fetch user statistics');
      const statsData = await statsResponse.json();
      setStatistics(statsData);

      // Fetch status history
      const historyResponse = await fetch(
        `${BASE_URL}/api/auth/status-history/email/${user.email}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!historyResponse.ok) throw new Error('Failed to fetch status history');
      const historyData = await historyResponse.json();
      setStatusHistory(historyData.history || []);

      setLoading(false);
    } catch (err) {
      setError('Failed to load user data');
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('Kein Token gefunden');
      
      // API-Aufruf zum Löschen des Benutzers
      const response = await fetch(`${BASE_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Löschen des Benutzers');
      }
      
      // Erfolgreiche Löschung
      setShowDeleteConfirm(false);
      
      // Callback zur Aktualisierung der Benutzerliste
      if (onUpdate) {
        onUpdate();
      }
      
      // Modal schließen
      onClose();
      
    } catch (err) {
      setDeleteError(err.message);
      setDeleteLoading(false);
    }
  };

  const renderDeleteConfirmDialog = () => (
    <div style={styles.confirmDialog}>
      <div style={styles.confirmTitle}>
        <AlertOctagon size={24} />
        Benutzer löschen
      </div>
      <div style={styles.confirmText}>
        Sind Sie sicher, dass Sie den Benutzer <strong>{user.username}</strong> löschen möchten? 
        Diese Aktion kann nicht rückgängig gemacht werden.
      </div>
      
      {deleteError && (
        <div style={styles.deleteError}>
          <AlertCircle size={16} />
          {deleteError}
        </div>
      )}
      
      <div style={styles.confirmButtons}>
        <button 
          style={styles.cancelButton}
          onClick={() => setShowDeleteConfirm(false)}
          disabled={deleteLoading}
        >
          Abbrechen
        </button>
        <button 
          style={styles.confirmDeleteButton}
          onClick={handleDeleteUser}
          disabled={deleteLoading}
        >
          {deleteLoading ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Activity size={16} />
              </motion.div>
              Löschen...
            </>
          ) : (
            <>
              <Trash2 size={16} />
              Löschen
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div style={styles.historySection}>
      <h3 style={styles.sectionTitle}>Status Verteilung</h3>
      {renderStatusChart()}
      
      <h3 style={styles.sectionTitle}>Status Verlauf</h3>
      {renderTimelineChart()}
      
      <div style={styles.historyList}>
        {statusHistory.map((record, index) => (
          <div key={index} style={styles.historyItem}>
            <div style={{
              ...styles.historyIcon,
              backgroundColor: STATUS_TYPES[record.newStatus]?.color
            }}>
              <Clock size={20} />
            </div>
            <div style={styles.historyContent}>
              <div style={styles.historyStatus}>
                {STATUS_TYPES[record.newStatus]?.label || record.newStatus}
              </div>
              <div style={styles.historyTime}>
                {new Date(record.timestamp).toLocaleString()}
              </div>
              <div style={styles.historyUpdater}>
                Geändert von: {record.updatedBy}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderOverview = () => (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {statistics?.statusDistribution && (
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '20px',
            borderRadius: '12px',
            height: '300px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>Status Verteilung</h3>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.statusDistribution).map(([status, count]) => ({
                    name: STATUS_TYPES[status]?.label || status,
                    value: count,
                    status: status
                  }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {Object.entries(statistics.statusDistribution).map((entry, index) => (
                    <Cell 
                      key={index} 
                      fill={STATUS_TYPES[entry[0]]?.color || '#6b7280'} 
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        <div style={{
          backgroundColor: '#f8fafc',
          padding: '20px',
          borderRadius: '12px',
          height: '300px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '16px'
          }}>Status Timeline</h3>
          <ResponsiveContainer>
            <LineChart data={statusHistory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp"
                tickFormatter={(timestamp) => new Date(timestamp).toLocaleDateString()}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(timestamp) => new Date(timestamp).toLocaleString()}
              />
              <Line
                type="stepAfter"
                dataKey="newStatus"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  if (!user) return null;

  return (
    <AnimatePresence>
      <motion.div
        style={styles.modalOverlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          style={styles.modal}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <div style={styles.header}>
            <h2>Benutzer Details</h2>
            <button onClick={onClose} style={styles.closeButton}>
              <X size={24} />
            </button>
          </div>

          <div style={styles.content}>
            <div style={styles.profileSection}>
              <div style={styles.imageContainer}>
                <img 
                  src={user.profileImagePath || '/api/placeholder/100/100'}
                  alt={user.username}
                  style={styles.profileImage}
                  onError={(e) => {
                    e.target.src = '/api/placeholder/100/100';
                    setImageLoaded(false);
                  }}
                />
              </div>

              <div style={styles.userInfo}>
                <h2 style={styles.userName}>{user.username}</h2>
                <div style={styles.userEmail}>
                  <Mail size={16} />
                  {user.email}
                </div>
                <div style={{
                  ...styles.statusBadge,
                  backgroundColor: STATUS_TYPES[user.status]?.color || '#6b7280'
                }}>
                  {STATUS_TYPES[user.status]?.label || user.status}
                </div>

                <button 
                  style={styles.deleteButton}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 size={16} />
                  Benutzer löschen
                </button>
              </div>
            </div>

            <div style={styles.tabsContainer}>
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'overview' ? styles.activeTab : {})
                }}
                onClick={() => setActiveTab('overview')}
              >
                Übersicht
              </button>
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'history' ? styles.activeTab : {})
                }}
                onClick={() => setActiveTab('history')}
              >
                Historie
              </button>
            </div>

            {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px'
              }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Activity size={24} />
                </motion.div>
              </div>
            ) : error ? (
              <div style={{
                padding: '20px',
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                borderRadius: '8px',
                marginTop: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertTriangle size={20} />
                {error}
              </div>
            ) : (
              <>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'history' && renderHistory()}
              </>
            )}
          </div>

          {showDeleteConfirm && renderDeleteConfirmDialog()}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const styles = {
  chartContainer: {
    marginTop: '20px',
    marginBottom: '24px',
  },
  historySection: {
    padding: '20px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '16px'
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '24px'
  },
  historyItem: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    alignItems: 'center'
  },
  historyIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff'
  },
  historyContent: {
    flex: 1
  },
  historyStatus: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '4px'
  },
  historyTime: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '4px'
  },
  historyUpdater: {
    fontSize: '14px',
    color: '#6b7280'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    overflow: 'hidden',
    position: 'relative'
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px'
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 80px)'
  },
  profileSection: {
    display: 'flex',
    gap: '24px',
    marginBottom: '32px',
    padding: '24px',
    backgroundColor: '#f8fafc',
    borderRadius: '16px'
  },
  imageContainer: {
    width: '300px',
    height: '300px',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  profileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  userInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  userName: {
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#1f2937'
  },
  userEmail: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#6b7280',
    marginBottom: '16px'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
    marginBottom: '16px'
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    marginTop: '16px'
  },
  deleteError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#fee2e2',
    color: '#ef4444',
    borderRadius: '4px',
    fontSize: '14px',
    marginBottom: '16px'
  },
  tabsContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    borderBottom: '1px solid #e5e7eb',
    padding: '0 24px'
  },
  tab: {
    padding: '12px 24px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#6b7280',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s'
  },
  activeTab: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6'
  },
  confirmDialog: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#ffffff',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '90%',
    maxWidth: '400px',
    zIndex: 1100
  },
  confirmTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  confirmText: {
    marginBottom: '24px',
    color: '#4b5563',
    lineHeight: 1.5
  },
  confirmButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  confirmDeleteButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '24px'
  },
  historyHeader: {
    backgroundColor: '#f8fafc',
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb'
  },
  historyCell: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '14px',
    color: '#4b5563'
  }
};

export default UserModal;